import {forwardRef, ReactNode, SyntheticEvent, useCallback, useEffect, useImperativeHandle, useMemo, useState} from 'react';
import './FilterDialog.less';
import Icons, {IconType} from '../../IconButton/Icons';
import IconButton from '../../IconButton/IconButton';
import Select from '../../Select/Select';
import SimpleSelect from '../../Select/SimpleSelect';
import type {SelectOption} from '@/@types/select';
import i18n from '../../../i18n/i18n';
import {TextField} from '@mui/material';
import type {
    ColumnFilter,
    EditableTableFilterValue,
    FilterCondition,
    FilterDialogHandle,
    FilterDialogProps,
    FilterOp,
    FilterResult,
    RelativeDateValue,
    SortStackEntry,
} from '@/@types/editableTable';
import type {EditableTableSortEntry} from '../EditableTable';

// =====================================================================================
// Operator catalog — per column type, what operators we expose, in mockup order.
// =====================================================================================

type OpMeta = {
    id: FilterOp,
    label: string,
    sym?: string,        // math-symbol pill for number ops
    valueless?: boolean, // empty / not_empty / ytd
    dual?: boolean,      // between
    relative?: boolean,  // relative
    isN?: boolean,       // top_n / bottom_n
};

type ColumnKind = 'string' | 'number' | 'money' | 'date' | 'boolean' | 'other';

const opLabel = (id: FilterOp): string => i18n.ls('filterDialog', `op_${id}`);

function operatorsForKind(kind: ColumnKind): OpMeta[] {
    switch (kind) {
        case 'number':
        case 'money':
            return [
                {id: 'equals',     label: opLabel('equals'),     sym: '='},
                {id: 'not_equals', label: opLabel('not_equals'), sym: '≠'},
                {id: 'gt',         label: opLabel('gt'),         sym: '>'},
                {id: 'gte',        label: opLabel('gte'),        sym: '≥'},
                {id: 'lt',         label: opLabel('lt'),         sym: '<'},
                {id: 'lte',        label: opLabel('lte'),        sym: '≤'},
                {id: 'between',    label: opLabel('between'),    sym: '↔', dual: true},
                {id: 'top_n',      label: opLabel('top_n'),      sym: '▲', isN: true},
                {id: 'bottom_n',   label: opLabel('bottom_n'),   sym: '▼', isN: true},
                {id: 'empty',      label: opLabel('empty'),                valueless: true},
                {id: 'not_empty',  label: opLabel('not_empty'),            valueless: true},
            ];
        case 'date':
            return [
                {id: 'on',        label: opLabel('on')},
                {id: 'before',    label: opLabel('before')},
                {id: 'after',     label: opLabel('after')},
                {id: 'between',   label: opLabel('between'),    dual: true},
                {id: 'relative',  label: opLabel('relative'),   relative: true},
                {id: 'ytd',       label: opLabel('ytd'),        valueless: true},
                {id: 'empty',     label: opLabel('empty'),      valueless: true},
                {id: 'not_empty', label: opLabel('not_empty'),  valueless: true},
            ];
        case 'string':
        case 'other':
        default:
            return [
                {id: 'contains',     label: opLabel('contains')},
                {id: 'not_contains', label: opLabel('not_contains')},
                {id: 'equals',       label: opLabel('equals')},
                {id: 'not_equals',   label: opLabel('not_equals')},
                {id: 'begins',       label: opLabel('begins')},
                {id: 'ends',         label: opLabel('ends')},
                {id: 'empty',        label: opLabel('empty'),     valueless: true},
                {id: 'not_empty',    label: opLabel('not_empty'), valueless: true},
            ];
    }
}

const getOpMeta = (kind: ColumnKind, op: FilterOp): OpMeta => {
    const list = operatorsForKind(kind);
    return list.find(o => o.id === op) ?? list[0];
};

/** Default relative-date selection when the user picks the "Relative date…" operator without choosing a window yet. */
const RELATIVE_DEFAULT: RelativeDateValue = 'this_month';

const RELATIVE_DATE_GROUPS: Array<{group: string, items: RelativeDateValue[]}> = [
    {group: i18n.ls('filterDialog', 'rel_group_day'),   items: ['today', 'yesterday', 'tomorrow']},
    {group: i18n.ls('filterDialog', 'rel_group_week'),  items: ['this_week', 'last_week', 'next_week']},
    {group: i18n.ls('filterDialog', 'rel_group_month'), items: ['this_month', 'last_month', 'next_month']},
    {group: i18n.ls('filterDialog', 'rel_group_year'),  items: ['this_year', 'last_year', 'next_year']},
];

const relLabel = (rel: RelativeDateValue): string => i18n.ls('filterDialog', `rel_${rel}`);

const TYPE_LABELS: Record<ColumnKind, string> = {
    string: 'typeText', number: 'typeNumber', money: 'typeMoney',
    date: 'typeDate', boolean: 'typeBoolean', other: 'typeOther',
};

// =====================================================================================
// Sort row — type-specific FontAwesome IconButtons per the user's design amendment.
// =====================================================================================

type SortDirection = 'asc' | 'desc' | undefined;

function sortIconsForKind(kind: ColumnKind): {ascIcon: IconType, descIcon: IconType, ascLabel: string, descLabel: string} {
    // Pick the icon that DEPICTS each direction. FontAwesome's `sort-alpha-down` shows
    // A↓Z (an ascending sort visually), so we use SORT_DOWN_ALPHA as the ascIcon — the
    // *visually* up-sloping `sort-alpha-down-alt` (SORT_UP_ALPHA) belongs on the desc button.
    // This must match EditableTable.getSortIcon so the column header and the dialog buttons
    // render identical icons for the same sort state.
    switch (kind) {
        case 'string':
            return {
                ascIcon:  Icons.SORT_DOWN_ALPHA,   // depicts A↓Z (ascending alphabetical)
                descIcon: Icons.SORT_UP_ALPHA,     // depicts Z↓A (descending alphabetical)
                ascLabel:  i18n.ls('filterDialog', 'sortAlphaAsc'),
                descLabel: i18n.ls('filterDialog', 'sortAlphaDesc'),
            };
        case 'number':
        case 'money':
            return {
                ascIcon:  Icons.SORT_DOWN_NUMERIC, // depicts 1↓9
                descIcon: Icons.SORT_UP_NUMERIC,   // depicts 9↓1
                ascLabel:  i18n.ls('filterDialog', 'sortNumericAsc'),
                descLabel: i18n.ls('filterDialog', 'sortNumericDesc'),
            };
        case 'date':
            return {
                ascIcon:  Icons.TIME_SORT_ASC,
                descIcon: Icons.TIME_SORT_DESC,
                ascLabel:  i18n.ls('filterDialog', 'sortDateAsc'),
                descLabel: i18n.ls('filterDialog', 'sortDateDesc'),
            };
        case 'boolean':
        case 'other':
        default:
            return {
                ascIcon:  Icons.SORT_UP,   // tagged 'ascend' in Icons.tsx
                descIcon: Icons.SORT_DOWN, // tagged 'descend'
                ascLabel:  i18n.ls('filterDialog', 'sortAsc'),
                descLabel: i18n.ls('filterDialog', 'sortDesc'),
            };
    }
}

type SortRowProps = {
    kind: ColumnKind,
    current: SortDirection,
    onChange: (next: SortDirection) => void,
};

function SortRow({kind, current, onChange}: SortRowProps) {
    const {ascIcon, descIcon, ascLabel, descLabel} = sortIconsForKind(kind);
    return (
        <div className="filter-dialog__sort-row fd-sortrow">
            <button type="button"
                    className={`fd-sortbtn ${current === 'asc' ? 'is-active' : ''}`}
                    aria-pressed={current === 'asc'}
                    onClick={() => onChange(current === 'asc' ? undefined : 'asc')}>
                <IconButton type={ascIcon} label={ascLabel} color={current === 'asc' ? 'cornflowerblue' : 'black'}/>
            </button>
            <button type="button"
                    className={`fd-sortbtn ${current === 'desc' ? 'is-active' : ''}`}
                    aria-pressed={current === 'desc'}
                    onClick={() => onChange(current === 'desc' ? undefined : 'desc')}>
                <IconButton type={descIcon} label={descLabel} color={current === 'desc' ? 'cornflowerblue' : 'black'}/>
            </button>
            <button type="button"
                    className="fd-sortbtn fd-sortbtn--ghost"
                    disabled={!current}
                    onClick={() => onChange(undefined)}>
                {i18n.ls('filterDialog', 'clearSort')}
            </button>
        </div>
    );
}

// =====================================================================================
// Operator select — Outlaw's <Select> wrapper, with each option's label as a ReactNode
// so the math-symbol pill (e.g. `≥`) renders inline next to the operator name.
// =====================================================================================

type OperatorSelectProps = {
    kind: ColumnKind,
    value: FilterOp,
    onChange: (op: FilterOp) => void,
    name: string,
};

function OperatorSelect({kind, value, onChange, name}: OperatorSelectProps) {
    const options = useMemo<SelectOption<FilterOp, OpMeta>[]>(() => operatorsForKind(kind).map(o => ({
        value: o.id,
        label: (
            <span className="fd-operator__label">
                {o.sym
                    ? <span className="fd-op-sym fd-op-sym--menu">{o.sym}</span>
                    : <span className="fd-op-sym fd-op-sym--menu fd-op-sym--blank"/>}
                <span>{o.label}</span>
            </span>
        ),
        data: o,
    })), [kind]);

    const defaultOption = options.find(o => o.value === value) ?? options[0];

    return (
        <div className="fd-operator">
            <Select
                name={`fd-op-${name}`}
                keyName={`fd-op-${name}`}
                options={options}
                defaultValue={defaultOption}
                isSearchable={false}
                allowNone={false}
                isMulti={false}
                disablePortaling={true}
                menuPlacement='auto'
                // Select's onChange callback receives the raw `value` (not the wrapped option)
                // when `usesExtraFields` is not enabled — see Select.tsx::onChangeSingle.
                onChange={(value: any) => {
                    if (typeof value === 'string' && value) onChange(value as FilterOp);
                }}
            />
        </div>
    );
}

// =====================================================================================
// Value input — switches on op metadata (valueless / dual / relative / N / single).
// =====================================================================================

type ValueInputProps = {
    kind: ColumnKind,
    op: FilterOp,
    value: string,
    value2: string,
    onChange: (v: string, v2?: string) => void,
};

function ValueInput({kind, op, value, value2, onChange}: ValueInputProps) {
    const meta = getOpMeta(kind, op);
    const moneyPrefix = kind === 'money' ? '$' : null;
    const inputType = (kind === 'number' || kind === 'money') ? 'number' : 'text';

    if (meta.valueless) {
        return (
            <div className="fd-value fd-value--none">
                <span className="fd-empty-hint">{i18n.ls('filterDialog', 'noValueNeeded')}</span>
            </div>
        );
    }

    if (meta.relative) {
        const currentRel = (value as RelativeDateValue) || RELATIVE_DEFAULT;
        const groupedOptions = RELATIVE_DATE_GROUPS.map(group => ({
            label: group.group,
            options: group.items.map(item => ({value: item, label: relLabel(item)})),
        }));
        const flatOptions: SelectOption<RelativeDateValue, void>[] = groupedOptions.flatMap(g => g.options);
        return (
            <div className="fd-value fd-value--relative">
                <Select
                    name="fd-relative-date"
                    keyName="fd-relative-date"
                    options={groupedOptions}
                    defaultValue={flatOptions.find(o => o.value === currentRel)}
                    isSearchable={false}
                    allowNone={false}
                    isMulti={false}
                    disablePortaling={true}
                    // Select's onChange callback receives the raw `value`, not the option object.
                    onChange={(value: any) => {
                        if (typeof value === 'string' && value) onChange(value);
                    }}
                />
            </div>
        );
    }

    if (meta.dual) {
        const dateInputType = kind === 'date' ? 'date' : inputType;
        return (
            <div className="fd-value fd-value--dual">
                <label className="fd-input">
                    {moneyPrefix && <span className="fd-input__prefix">{moneyPrefix}</span>}
                    <input type={dateInputType}
                           value={value}
                           placeholder="min"
                           onChange={(e) => onChange(e.target.value, value2)}/>
                </label>
                <span className="fd-and">and</span>
                <label className="fd-input">
                    {moneyPrefix && <span className="fd-input__prefix">{moneyPrefix}</span>}
                    <input type={dateInputType}
                           value={value2}
                           placeholder="max"
                           onChange={(e) => onChange(value, e.target.value)}/>
                </label>
            </div>
        );
    }

    if (meta.isN) {
        return (
            <div className="fd-value">
                <label className="fd-input fd-input--n">
                    <span className="fd-input__prefix">N =</span>
                    <input type="number"
                           min="1"
                           value={value || '10'}
                           onChange={(e) => onChange(e.target.value)}/>
                </label>
            </div>
        );
    }

    if (kind === 'date') {
        return (
            <div className="fd-value">
                <label className="fd-input">
                    <input type="date"
                           value={value}
                           onChange={(e) => onChange(e.target.value)}/>
                </label>
            </div>
        );
    }

    // Plain single value
    return (
        <div className="fd-value">
            <label className="fd-input">
                {moneyPrefix && <span className="fd-input__prefix">{moneyPrefix}</span>}
                <TextField variant="standard"
                           type={inputType}
                           value={value}
                           onChange={(e) => onChange(e.target.value)}
                           slotProps={{input: {disableUnderline: true}}}
                           fullWidth
                           autoFocus
                />
            </label>
        </div>
    );
}

// =====================================================================================
// Boolean tri-state segmented control.
// =====================================================================================

type BoolControlProps = {
    value: 'true' | 'false' | 'either',
    onChange: (next: 'true' | 'false' | 'either') => void,
};

function BoolControl({value, onChange}: BoolControlProps) {
    const options: Array<{v: 'true' | 'false' | 'either', label: string}> = [
        {v: 'true',   label: i18n.ls('filterDialog', 'true')},
        {v: 'false',  label: i18n.ls('filterDialog', 'false')},
        {v: 'either', label: i18n.ls('filterDialog', 'either')},
    ];
    return (
        <div className="fd-segment fd-segment--bool" role="radiogroup">
            {options.map(o => (
                <button key={o.v}
                        type="button"
                        role="radio"
                        aria-checked={value === o.v}
                        className={`fd-segment__btn ${value === o.v ? 'is-active' : ''}`}
                        onClick={() => onChange(o.v)}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

// =====================================================================================
// Value checklist — text columns "Limit to values".
// =====================================================================================

type ValueChecklistProps = {
    choices: EditableTableFilterValue[],
    selected: string[] | undefined,
    onChange: (next: string[] | undefined) => void,
    /** When true, render the AND/OR segmented control next to the section header. */
    showJoin?: boolean,
    join?: 'and' | 'or',
    onJoinChange?: (join: 'and' | 'or') => void,
};

function ValueChecklist({choices, selected, onChange, showJoin, join, onJoinChange}: ValueChecklistProps) {
    const [search, setSearch] = useState('');

    const visible = useMemo(() => {
        const s = search.trim().toLowerCase();
        const list = choices.map(c => String(c));
        return s ? list.filter(c => c.toLowerCase().includes(s)) : list;
    }, [choices, search]);

    const selectedSet = useMemo(() => new Set(selected ?? []), [selected]);
    const allSelected = visible.length > 0 && visible.every(v => selectedSet.has(v));
    const someSelected = visible.some(v => selectedSet.has(v));

    const toggle = (v: string) => {
        const next = new Set(selectedSet);
        if (next.has(v)) next.delete(v); else next.add(v);
        onChange(next.size === choices.length ? undefined : Array.from(next));
    };
    const toggleAll = () => {
        if (allSelected) onChange([]);
        else onChange(undefined); // undefined === "all selected, no filter"
    };

    return (
        <div className="fd-checklist">
            <div className="fd-section__head">
                <span className="fd-section__label">{i18n.ls('filterDialog', 'limitToValues')}</span>
                {showJoin && (
                    <div className="fd-segment fd-segment--join fd-checklist__join" role="radiogroup" aria-label="Combine checked values with AND or OR">
                        <button type="button"
                                className={`fd-segment__btn ${join === 'and' ? 'is-active' : ''}`}
                                aria-pressed={join === 'and'}
                                onClick={() => onJoinChange?.('and')}>
                            {i18n.ls('filterDialog', 'and')}
                        </button>
                        <button type="button"
                                className={`fd-segment__btn ${(!join || join === 'or') ? 'is-active' : ''}`}
                                aria-pressed={!join || join === 'or'}
                                onClick={() => onJoinChange?.('or')}>
                            {i18n.ls('filterDialog', 'or')}
                        </button>
                    </div>
                )}
                <span className="fd-section__hint">
                    {selected === undefined ? `${choices.length} of ${choices.length}` : `${selectedSet.size} of ${choices.length}`} selected
                </span>
            </div>
            <label className="fd-input fd-input--search">
                <input type="text"
                       value={search}
                       placeholder={i18n.ls('filterDialog', 'searchValues')}
                       onChange={(e) => setSearch(e.target.value)}/>
            </label>
            <ul className="fd-check-list">
                <li className="fd-check-list__item fd-check-list__item--all" onClick={toggleAll}>
                    <span className={`fd-check ${allSelected ? 'is-checked' : someSelected ? 'fd-check--mixed' : ''}`}>
                        {allSelected ? '✓' : someSelected ? <span/> : null}
                    </span>
                    <span>{i18n.ls('filterDialog', 'selectAll')}</span>
                </li>
                {visible.map(v => (
                    <li key={v || '__blank__'} className="fd-check-list__item" onClick={() => toggle(v)}>
                        <span className={`fd-check ${selectedSet.has(v) ? 'is-checked' : ''}`}>
                            {selectedSet.has(v) ? '✓' : null}
                        </span>
                        <span className={`fd-check-list__label ${v === '' ? 'fd-check-list__label--blank' : ''}`}>
                            {v === '' ? '(blank)' : v}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// =====================================================================================
// Sort Manager — full sort priority list with reorder + drag-drop.
// Only renders when more than one column is currently in the sort stack (or this dialog's
// column shares the stack with others); a single-column stack doesn't need management UI.
// =====================================================================================

type SortManagerProps = {
    stack: SortStackEntry[],
    /** Current column's label — highlighted in the list so users see "this one is me". */
    myLabel: string,
    /** Every sortable column in the table, used for the "Add a column..." dropdown and type-aware icons.
     * `displayLabel` may be a ReactNode for columns whose headers carry icons/markup — rendered inline. */
    sortableColumns: Array<{label: string, displayLabel: ReactNode, kind: ColumnKind}>,
    onMove: (from: number, to: number) => void,
    onRemove: (idx: number) => void,
    onToggleOrder: (idx: number) => void,
    onAdd: (label: string) => void,
};

function SortManager({stack, myLabel, sortableColumns, onMove, onRemove, onToggleOrder, onAdd}: SortManagerProps) {
    const [dragFrom, setDragFrom] = useState<number | null>(null);
    const [dragOver, setDragOver] = useState<number | null>(null);
    // Bump on every selection so the add-column <Select> remounts and resets to its placeholder.
    const [addSelectNonce, setAddSelectNonce] = useState<number>(0);

    // Resolve a label → user-facing display name (may be a ReactNode with inline markup).
    // Falls back to the label itself when the sortableColumns list doesn't include the entry (defensive).
    const displayFor = (label: string): ReactNode => {
        return sortableColumns.find(c => c.label === label)?.displayLabel ?? label;
    };

    // Resolve a label → its column kind for picking the right sort icon (alpha / numeric / date / generic).
    const kindFor = (label: string): ColumnKind => {
        return sortableColumns.find(c => c.label === label)?.kind ?? 'other';
    };

    // Columns not yet in the stack — candidates for the "Add a column..." dropdown.
    const addable = sortableColumns.filter(col => !stack.some(e => e.label === col.label));

    // Render whenever the user has at least one sort active. Empty stack means "no sort";
    // the Add affordance shows up only after the user kicks off sorting by clicking asc/desc
    // on this column. Keeps the empty-state dialog visually quiet.
    if (stack.length === 0) return null;

    const handleDragStart = (idx: number) => (e: React.DragEvent) => {
        setDragFrom(idx);
        e.dataTransfer.effectAllowed = 'move';
        // Setting any data is required by Firefox to start the drag.
        e.dataTransfer.setData('text/plain', String(idx));
    };
    const handleDragOver = (idx: number) => (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOver !== idx) setDragOver(idx);
    };
    const handleDrop = (idx: number) => (e: React.DragEvent) => {
        e.preventDefault();
        if (dragFrom !== null && dragFrom !== idx) onMove(dragFrom, idx);
        setDragFrom(null);
        setDragOver(null);
    };
    const handleDragEnd = () => {
        setDragFrom(null);
        setDragOver(null);
    };

    return (
        <div className="fd-sort-manager">
            <div className="fd-section__head">
                <span className="fd-section__label">{i18n.ls('filterDialog', 'sortPriority')}</span>
                <span className="fd-section__hint">{i18n.ls('filterDialog', 'sortPriorityHelp')}</span>
            </div>
            <ol className="fd-sort-manager__list">
                {stack.map((entry, idx) => {
                    const isMe = entry.label === myLabel;
                    const isDragging = dragFrom === idx;
                    const isDropTarget = dragOver === idx && dragFrom !== null && dragFrom !== idx;
                    const display = displayFor(entry.label);
                    const kind = kindFor(entry.label);
                    // Type-aware sort icons — alpha for text, numeric for number/money,
                    // calendar-clock for date, plain arrows for boolean/other.
                    const {ascIcon, descIcon} = sortIconsForKind(kind);
                    const currentIcon = entry.order === 'asc' ? ascIcon : descIcon;
                    return (
                        <li key={entry.label}
                            className={`fd-sort-manager__item ${isMe ? 'fd-sort-manager__item--me' : ''} ${isDragging ? 'is-dragging' : ''} ${isDropTarget ? 'is-drop-target' : ''}`}
                            draggable
                            onDragStart={handleDragStart(idx)}
                            onDragOver={handleDragOver(idx)}
                            onDrop={handleDrop(idx)}
                            onDragEnd={handleDragEnd}>
                            <span className="fd-sort-manager__drag" aria-hidden="true" title={i18n.ls('filterDialog', 'dragToReorder')}>
                                <IconButton type={Icons.GRIP}/>
                            </span>
                            <span className="fd-sort-manager__position">{idx + 1}</span>
                            <span className="fd-sort-manager__label">{display}{isMe ? ` (${i18n.ls('filterDialog', 'thisColumn')})` : ''}</span>
                            <button type="button"
                                    className="fd-sort-manager__order"
                                    aria-label={`Toggle sort direction for ${entry.label}`}
                                    title={entry.order === 'asc'
                                        ? i18n.ls('filterDialog', 'sortedAscClickToReverse')
                                        : i18n.ls('filterDialog', 'sortedDescClickToReverse')}
                                    onClick={() => onToggleOrder(idx)}>
                                <IconButton type={currentIcon}/>
                            </button>
                            <button type="button"
                                    className="fd-iconbtn fd-iconbtn--danger fd-sort-manager__remove"
                                    aria-label={`Remove ${entry.label} from sort`}
                                    onClick={() => onRemove(idx)}>
                                ×
                            </button>
                        </li>
                    );
                })}
            </ol>

            {addable.length > 0 && (
                <div className="fd-sort-manager__add">
                    {/* Bump nonce on every pick so the Select remounts back to its placeholder.
                        We never want this to retain a selected value — it's an action menu, not a value picker. */}
                    <Select
                        key={addSelectNonce}
                        name={`fd-add-sort-${addSelectNonce}`}
                        keyName={`fd-add-sort-${addSelectNonce}`}
                        // Wrap displayLabel in a span so SelectOption's label type accepts it whether
                        // it's a plain string, a ReactElement, or an array of nodes (e.g. icon + text).
                        options={addable.map(col => ({value: col.label, label: <span>{col.displayLabel}</span>}))}
                        placeholder={i18n.ls('filterDialog', 'addSortColumn')}
                        isSearchable={false}
                        allowNone={false}
                        isMulti={false}
                        isClearable={false}
                        disablePortaling={true}
                        menuPlacement='auto'
                        clsName='fd-sort-manager__add-select'
                        // Select passes the raw `value`, not the option object.
                        onChange={(value: any) => {
                            if (typeof value === 'string' && value) {
                                onAdd(value);
                                setAddSelectNonce(n => n + 1);
                            }
                        }}/>
                </div>
            )}
        </div>
    );
}

// =====================================================================================
// Helpers — convert between the persisted ColumnFilter shape and the dialog's draft state.
// =====================================================================================

type DraftCondition = {
    /** Stable id so React keys survive removeCondition (so the operator Select doesn't
     * inherit the removed condition's internal state). Generated on construction. */
    id: number,
    op: FilterOp,
    value: string,
    value2: string,
};

let nextConditionId = 1;
const makeCondition = (op: FilterOp, value: string = '', value2: string = ''): DraftCondition => ({
    id: nextConditionId++,
    op, value, value2,
});

const defaultOpForKind = (kind: ColumnKind): FilterOp => {
    if (kind === 'number' || kind === 'money') return 'gte';
    if (kind === 'date') return 'on';
    return 'contains';
};

function conditionToDraft(c: FilterCondition): DraftCondition {
    return makeCondition(
        c.op,
        c.value === undefined || c.value === null ? '' : String(c.value),
        c.value2 === undefined || c.value2 === null ? '' : String(c.value2),
    );
}

function draftToCondition(d: DraftCondition): FilterCondition {
    const out: FilterCondition = {op: d.op};
    if (d.value !== '') out.value = d.value;
    if (d.value2 !== '') out.value2 = d.value2;
    return out;
}

function isDraftActive(d: DraftCondition): boolean {
    // valueless ops are active by themselves
    if (d.op === 'empty' || d.op === 'not_empty' || d.op === 'ytd') return true;
    // dual ops require both
    if (d.op === 'between') return d.value !== '' && d.value2 !== '';
    return d.value !== '';
}

// =====================================================================================
// Pure sort-stack mutators — exported so they can be unit-tested independently from the
// dialog. Each returns a NEW array; never mutates the input. The dialog's hooks call these
// inside `setDraftStack(prev => fn(prev, ...))` for stable behaviour under React batching.
// =====================================================================================

/** Move the entry at `from` to position `to`. No-op when indices are out of range or equal. */
export function moveStackEntry(stack: SortStackEntry[], from: number, to: number): SortStackEntry[] {
    if (from === to || from < 0 || to < 0 || from >= stack.length || to >= stack.length) return stack;
    const copy = [...stack];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
}

/** Remove the entry at `idx`. Returns the same reference when idx is out of range. */
export function removeStackEntry(stack: SortStackEntry[], idx: number): SortStackEntry[] {
    if (idx < 0 || idx >= stack.length) return stack;
    return stack.filter((_, i) => i !== idx);
}

/** Flip the direction of the entry at `idx` (asc ↔ desc). */
export function toggleStackEntryOrder(stack: SortStackEntry[], idx: number): SortStackEntry[] {
    if (idx < 0 || idx >= stack.length) return stack;
    return stack.map((e, i) => i === idx ? {...e, order: e.order === 'asc' ? 'desc' : 'asc'} : e);
}

/** Append `label` to the end of the stack (lowest priority) with default asc order. Dedup'd. */
export function addStackEntry(stack: SortStackEntry[], label: string): SortStackEntry[] {
    if (stack.some(e => e.label === label)) return stack;
    return [...stack, {label, order: 'asc'}];
}

/**
 * Set `label`'s order in the stack. When `order` is undefined the entry is removed; when the
 * column wasn't in the stack it's added at the FRONT (highest priority) — that's the natural
 * outcome of clicking asc/desc on a column the user is actively focused on in the dialog.
 */
export function setStackEntryOrder(stack: SortStackEntry[], label: string, order: 'asc' | 'desc' | undefined): SortStackEntry[] {
    const idx = stack.findIndex(e => e.label === label);
    if (order === undefined) {
        if (idx === -1) return stack;
        const copy = [...stack];
        copy.splice(idx, 1);
        return copy;
    }
    const entry: SortStackEntry = {label, order};
    if (idx === -1) return [entry, ...stack];
    const copy = [...stack];
    copy[idx] = entry;
    return copy;
}

// =====================================================================================
// Main FilterDialog component.
// =====================================================================================

const FilterDialog = forwardRef(<T,>(props: FilterDialogProps<T>, ref: React.Ref<FilterDialogHandle<T>>) => {
    const sortProp = props.sortProp;
    const kind: ColumnKind = ((sortProp?.type as ColumnKind) || 'other');
    const initialFilter = props.currentFilter;

    // Sort priority list (Excel-style multi-column sort). The dialog manages the full draft
    // stack, not just this column's entry — that way the user can reorder / remove other
    // sorts without leaving this column's filter UI.
    const [draftStack, setDraftStack] = useState<SortStackEntry[]>(props.currentSortStack ?? []);
    // The current column's slot in the stack (if any).
    const myStackEntry = draftStack.find(e => e.label === props.label) ?? null;
    const myStackIndex = myStackEntry ? draftStack.findIndex(e => e.label === props.label) : -1;
    const sortOrder: SortDirection = (myStackEntry?.order ?? undefined) as SortDirection;

    // Boolean tri-state: read from the persisted filter, default to 'either' (no filter).
    const [boolValue, setBoolValue] = useState<'true' | 'false' | 'either'>(initialFilter?.boolValue ?? 'either');

    // Conditions list, max 2 with AND/OR join.
    const [conditions, setConditions] = useState<DraftCondition[]>(
        initialFilter && initialFilter.conditions?.length
            ? initialFilter.conditions.map(conditionToDraft)
            : kind === 'boolean' ? [] : [makeCondition(defaultOpForKind(kind))]
    );
    const [join, setJoin] = useState<'and' | 'or'>(initialFilter?.join ?? 'and');

    // Optional value checklist — shown whenever the caller/auto-derive supplied choices,
    // regardless of column type. Numeric columns (Risk, Priority) and text/other columns
    // (Stage, Team, Client) all get the checklist when their distinct values are categorical.
    const [checklistSelection, setChecklistSelection] = useState<string[] | undefined>(initialFilter?.checklist);
    // AND/OR for the checklist — only meaningful on multi-value columns (e.g. Team). The
    // toggle UI is only rendered when `isMultiValueColumn` is true; atomic cells can't satisfy
    // an AND of multiple distinct values, so it'd just produce empty results and confuse.
    const [checklistJoin, setChecklistJoin] = useState<'and' | 'or'>(initialFilter?.checklistJoin ?? 'or');
    const showChecklist = (props.checklistChoices?.length ?? 0) > 0;

    // Build the current ColumnFilter from draft state.
    const buildFilter = useCallback((): ColumnFilter | null => {
        if (kind === 'boolean') {
            if (boolValue === 'either') return null;
            return {
                label: props.label,
                conditions: [],
                boolValue,
            };
        }

        const active = conditions.filter(isDraftActive);
        const checklistActive = showChecklist && checklistSelection && checklistSelection.length > 0;
        if (active.length === 0 && !checklistActive) return null;

        const out: ColumnFilter = {
            label: props.label,
            conditions: active.map(draftToCondition),
        };
        if (active.length === 2) out.join = join;
        if (checklistActive) {
            out.checklist = checklistSelection;
            // Only persist checklistJoin when the column is actually multi-value AND the user
            // chose AND — otherwise the engine's default OR is correct.
            if (props.isMultiValueColumn && checklistJoin === 'and') out.checklistJoin = 'and';
        }
        return out;
    }, [kind, props.label, boolValue, conditions, join, showChecklist, checklistSelection, checklistJoin, props.isMultiValueColumn]);

    const draftFilter = useMemo(() => buildFilter(), [buildFilter]);

    // Push the draft up to the parent so it can live-filter the table behind the dialog.
    const {onDraftChange, onSortDraftChange} = props;
    useEffect(() => {
        onDraftChange?.(draftFilter);
    }, [draftFilter, onDraftChange]);

    // Push the full draft sort stack on every change so the table re-sorts behind the dialog.
    useEffect(() => {
        onSortDraftChange?.(draftStack);
    }, [draftStack, onSortDraftChange]);

    // ---- Sort stack mutation helpers, used by the dialog's sort buttons and Sort Manager. ----

    /** Set this column's order to `next`. Removes if next is undefined; adds at front if absent. */
    const setMyOrder = useCallback((next: 'asc' | 'desc' | undefined) => {
        setDraftStack(prev => setStackEntryOrder(prev, props.label, next));
    }, [props.label]);

    /** Reorder the stack: move entry at `from` to position `to`. */
    const onMove = useCallback((from: number, to: number) => {
        setDraftStack(prev => moveStackEntry(prev, from, to));
    }, []);

    /** Remove a stack entry by index. */
    const onRemove = useCallback((idx: number) => {
        setDraftStack(prev => removeStackEntry(prev, idx));
    }, []);

    /** Flip a stack entry's direction in place (asc ↔ desc). */
    const onToggleOrder = useCallback((idx: number) => {
        setDraftStack(prev => toggleStackEntryOrder(prev, idx));
    }, []);

    /** Append a new column to the end of the stack (lowest priority) with default asc order. */
    const onAdd = useCallback((label: string) => {
        setDraftStack(prev => addStackEntry(prev, label));
    }, []);

    // Live match-summary for the footer.
    // Only depend on the specific callable + total — not the entire `props` object — otherwise
    // every parent re-render (e.g. MUI Dialog transitions) re-runs matchCount over every item,
    // which can hang the app for columns with heavy renderItemFn.
    const {matchCount, totalCount} = props;
    const matchSummary = useMemo(() => {
        if (!matchCount || totalCount === undefined) return null;
        const n = matchCount(draftFilter);
        // matchCount can transiently return undefined during the parent's first render
        // after `handleFilterClick` sets state (the cached evaluator may not yet have a
        // value for the in-flight draft). Guard so the i18n template never receives
        // undefined — that would throw a TypeError that re-renders the dialog and
        // turns into a "Maximum update depth exceeded" infinite loop.
        if (typeof n !== 'number' || Number.isNaN(n)) return null;
        return i18n.l('filterDialog', 'matchSummary', {n, total: totalCount}) as string;
    }, [matchCount, totalCount, draftFilter]);

    useImperativeHandle(ref, () => ({
        getFilter: (): FilterResult<T> => {
            const filter = buildFilter();
            const updatedSortProp: EditableTableSortEntry<T> = sortProp
                ? {...sortProp, order: (sortOrder ?? '') as 'asc' | 'desc' | ''}
                : sortProp;
            return {
                isSorted: draftStack.length > 0,
                sortStack: draftStack,
                sortProp: updatedSortProp,
                isFiltered: filter !== null,
                filter,
            };
        },
    }), [buildFilter, sortProp, sortOrder, draftStack]);

    // ---- Condition editing handlers ----

    const updateCondition = (idx: number, patch: Partial<DraftCondition>) => {
        setConditions(prev => prev.map((c, i) => i === idx ? {...c, ...patch} : c));
    };
    const removeCondition = (idx: number) => {
        setConditions(prev => prev.filter((_, i) => i !== idx));
    };
    const addCondition = () => {
        setConditions(prev => prev.length >= 2 ? prev : [...prev, makeCondition(defaultOpForKind(kind))]);
    };

    const clearAll = useCallback(() => {
        // Preserve the operator on each condition — clearing only the values. This avoids
        // a desync between our `conditions` state and the <Select> components' internal
        // state, and matches the intuition that "Clear filter" empties the criteria, not
        // the user's choice of operator. The sort stack is left alone too — there's a
        // separate "Clear sort" affordance.
        setConditions(prev => {
            if (kind === 'boolean') return [];
            if (prev.length === 0) return [makeCondition(defaultOpForKind(kind))];
            return prev.map(c => ({...c, value: '', value2: ''}));
        });
        setJoin('and');
        setBoolValue('either');
        setChecklistSelection(undefined);
    }, [kind]);

    const onApply = useCallback(() => {
        const filter = buildFilter();
        const updatedSortProp: EditableTableSortEntry<T> = sortProp
            ? {...sortProp, order: (sortOrder ?? '') as 'asc' | 'desc' | ''}
            : sortProp;
        props.onOk({
            isSorted: draftStack.length > 0,
            sortStack: draftStack,
            sortProp: updatedSortProp,
            isFiltered: filter !== null,
            filter,
        });
    }, [buildFilter, sortProp, sortOrder, draftStack, props]);

    const onCancel = useCallback((e?: SyntheticEvent) => {
        e?.preventDefault();
        props.onCancel?.();
    }, [props]);

    // ---- Render ----

    const typeChipLabel = i18n.ls('filterDialog', TYPE_LABELS[kind]);
    const columnLabel: ReactNode = props.displayLabel ?? props.label;
    const columnLabelStr = typeof columnLabel === 'string' ? columnLabel : props.label;

    return (
        <div className={`filter-dialog fd fd--type-${kind}`} role="dialog" aria-label={`${i18n.ls('filterDialog', 'filterForField')} ${columnLabelStr}`}>
            <header className="fd-header">
                <div className="fd-header__left">
                    <h2 className="fd-title">
                        <span className="fd-title__verb">{i18n.ls('filterDialog', 'filterForField')} </span>
                        <span className="fd-title__col">{columnLabel}</span>
                    </h2>
                </div>
                <button type="button" className="fd-iconbtn" aria-label={i18n.ls('filterDialog', 'close')} onClick={onCancel}>
                    ✕
                </button>
            </header>

            <div className="fd-body">
                {/* Type — declares what kind of values this column holds so the operators below make sense. */}
                <section className="fd-section">
                    <div className="fd-section__head">
                        <span className="fd-section__label">{i18n.ls('filterDialog', 'type')}</span>
                    </div>
                    <div className="fd-type-value">{typeChipLabel}</div>
                </section>

                <div className="fd-divider"/>

                {/* Sort */}
                <section className="fd-section">
                    <div className="fd-section__head">
                        <span className="fd-section__label">{i18n.ls('filterDialog', 'sort')}</span>
                    </div>
                    <SortRow kind={kind} current={sortOrder} onChange={setMyOrder}/>
                    {myStackIndex >= 0 && draftStack.length > 1 && (
                        <div className="fd-sort-priority-hint">
                            {i18n.l('filterDialog', 'sortPriorityHint', {position: myStackIndex + 1, total: draftStack.length}) as string}
                        </div>
                    )}
                    <SortManager
                        stack={draftStack}
                        myLabel={props.label}
                        sortableColumns={props.sortableColumns ?? []}
                        onMove={onMove}
                        onRemove={onRemove}
                        onToggleOrder={onToggleOrder}
                        onAdd={onAdd}
                    />
                </section>

                <div className="fd-divider"/>

                {/* Filter */}
                <section className="fd-section">
                    <div className="fd-section__head">
                        <span className="fd-section__label">{i18n.ls('filterDialog', 'filter')}</span>
                        {kind !== 'boolean' && conditions.filter(isDraftActive).length === 0 && (
                            <span className="fd-section__hint">{i18n.ls('filterDialog', 'searchHint', columnLabelStr)}</span>
                        )}
                    </div>

                    {kind === 'boolean' ? (
                        <div className="fd-bool">
                            <span className="fd-bool__label">
                                {i18n.l('filterDialog', 'boolPrompt', columnLabelStr) as ReactNode}
                            </span>
                            <BoolControl value={boolValue} onChange={setBoolValue}/>
                        </div>
                    ) : (
                        <div className="fd-conditions">
                            {conditions.map((c, i) => (
                                <div key={c.id} className="fd-condition-wrap">
                                    {i > 0 && (
                                        <div className="fd-join">
                                            <div className="fd-join__line"/>
                                            <div className="fd-segment fd-segment--join">
                                                <button type="button"
                                                        className={`fd-segment__btn ${join === 'and' ? 'is-active' : ''}`}
                                                        onClick={() => setJoin('and')}>
                                                    {i18n.ls('filterDialog', 'and')}
                                                </button>
                                                <button type="button"
                                                        className={`fd-segment__btn ${join === 'or' ? 'is-active' : ''}`}
                                                        onClick={() => setJoin('or')}>
                                                    {i18n.ls('filterDialog', 'or')}
                                                </button>
                                            </div>
                                            <div className="fd-join__line"/>
                                        </div>
                                    )}
                                    <div className="fd-condition">
                                        <div className="fd-condition__no">{i + 1}</div>
                                        <div className="fd-condition__row">
                                            <OperatorSelect
                                                kind={kind}
                                                value={c.op}
                                                name={`condition-${i}-op`}
                                                onChange={(op) => updateCondition(i, {op, value: '', value2: ''})}
                                            />
                                            <ValueInput
                                                kind={kind}
                                                op={c.op}
                                                value={c.value}
                                                value2={c.value2}
                                                onChange={(v, v2) => updateCondition(i, {value: v, value2: v2 ?? c.value2})}
                                            />
                                            {conditions.length > 1 && (
                                                <button type="button"
                                                        className="fd-iconbtn fd-iconbtn--danger"
                                                        aria-label="Remove condition"
                                                        onClick={() => removeCondition(i)}>
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {conditions.length < 2 && (
                                <button type="button" className="fd-add" onClick={addCondition}>
                                    <span aria-hidden="true">＋</span>
                                    <span>{conditions.length === 0
                                        ? i18n.ls('filterDialog', 'addCondition')
                                        : i18n.ls('filterDialog', 'addAnotherCondition')}</span>
                                </button>
                            )}
                        </div>
                    )}
                </section>

                {showChecklist && (
                    <>
                        <div className="fd-divider"/>
                        <section className="fd-section">
                            <ValueChecklist
                                choices={props.checklistChoices ?? []}
                                selected={checklistSelection}
                                onChange={setChecklistSelection}
                                showJoin={!!props.isMultiValueColumn}
                                join={checklistJoin}
                                onJoinChange={setChecklistJoin}
                            />
                        </section>
                    </>
                )}
            </div>

            <footer className="fd-footer">
                <button type="button" className="fd-footer__clear" onClick={clearAll}>
                    {i18n.ls('filterDialog', 'clearFilter')}
                </button>
                <div className="fd-footer__right">
                    {matchSummary && <span className="fd-match">{matchSummary}</span>}
                    <button type="button" className="fd-btn fd-btn--ghost" onClick={onCancel}>
                        {i18n.ls('filterDialog', 'cancel')}
                    </button>
                    <button type="button" className="fd-btn fd-btn--primary" onClick={onApply}>
                        {i18n.ls('filterDialog', 'apply')}
                    </button>
                </div>
            </footer>
        </div>
    );
});

export default FilterDialog;
